import { useEffect, useRef, useState } from "react";

interface SearchReader {
  get(key: string): string | null;
}

/**
 * Key that changes only when the URL changes from outside the component
 * (history, agent navigation, a link). Changes the component itself announces
 * through `markSelfNavigation` update the URL without a remount, so a filter
 * input keeps focus.
 */
export function useExternalUrlKey(urlKey: string, search: SearchReader) {
  const [generation, setGeneration] = useState(0);
  const pendingRef = useRef<Record<string, string | undefined> | null>(null);
  const seenRef = useRef(urlKey);

  useEffect(() => {
    if (urlKey === seenRef.current) return;
    seenRef.current = urlKey;

    const pending = pendingRef.current;
    pendingRef.current = null;
    if (
      pending &&
      Object.entries(pending).every(
        ([key, value]) => (search.get(key) ?? undefined) === value,
      )
    ) {
      return;
    }

    setGeneration((value) => value + 1);
  }, [urlKey, search]);

  return {
    key: generation,
    markSelfNavigation: (next: Record<string, string | undefined>) => {
      pendingRef.current = next;
    },
  };
}
